if (!customElements.get('so-sticky-atc')) {
  class SoStickyAtc extends HTMLElement {
    connectedCallback() {
      this.product = this.closest('.product') || document;
      this.mainForm = this.product.querySelector('.js-product-form-main');
      this.mainBtn = this.mainForm && this.mainForm.querySelector('[name="add"]');
      if (!this.mainForm || !this.mainBtn) return; // nothing to proxy; stay hidden

      this.btn = this.querySelector('.js-so-sticky-btn');
      this.priceEl = this.querySelector('.js-so-sticky-price');
      this.variantEl = this.querySelector('.js-so-sticky-variant');
      this.thumbEl = this.querySelector('.js-so-sticky-thumb');
      this.infoEl = this.querySelector('.so-sticky-atc__info');
      this.mainPrice = this.product.querySelector('.product-info__price > .price');
      this.variantPicker = this.product.querySelector('variant-picker');
      this.optionCount = parseInt(this.dataset.optionCount || '0', 10);
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      // P0-2B: summary card (thumb, flavour, "Qty N", Edit) — one tap target back to the buy box.
      this.variantCount = parseInt(this.dataset.variantCount || '0', 10);
      this.card = this.querySelector('.js-so-sticky-card');
      this.cardThumb = this.querySelector('.js-so-sticky-card-thumb');
      this.cardName = this.querySelector('.js-so-sticky-card-name');
      this.qtyBadge = this.querySelector('.js-so-sticky-qty-badge');
      this.pdp = this.mainForm.querySelector('so-preorder-pdp');

      this.removeAttribute('hidden'); // present but translated off-screen until .is-visible
      this.setVisible(false);
      this.syncPrice();
      this.syncButton();

      // Show once the main ATC button has scrolled above the viewport top (passed).
      // A rAF-throttled scroll check is reliable at any scroll speed — an
      // IntersectionObserver can miss a fast scroll that skips the intersecting phase.
      this.onScroll = () => {
        if (this._raf) return;
        this._raf = requestAnimationFrame(() => {
          this._raf = null;
          this.setVisible(this.mainBtn.getBoundingClientRect().bottom < 0);
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.onScroll();

      // Mirror the main button's state (disabled / label) into ours.
      this.mo = new MutationObserver(() => this.syncButton());
      this.mo.observe(this.mainBtn, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class', 'data-so-disabled'],
        childList: true,
        characterData: true,
        subtree: true,
      });

      // Live price + variant-name updates when a variant resolves.
      if (this.variantPicker) {
        this.variantPicker.addEventListener('on:variant:change', (e) => {
          this.syncPrice();
          this.syncButton();
          this.setVariantName(e.detail && e.detail.variant);
        });
      }

      this.btn.addEventListener('click', () => this.onClick());

      // P0-2B: the card → back to the buy box, where every product type is edited with its own controls.
      if (this.card) this.card.addEventListener('click', () => this.editInBuyBox());
      // Re-read the shared buy state whenever the buy box changes it (variant, qty, after add).
      this._onBuyState = (e) => { if (!this.pdp || e.target === this.pdp) this.syncInfo(); };
      document.addEventListener('so:buy-state', this._onBuyState);
      if (window.customElements && customElements.whenDefined) customElements.whenDefined('so-preorder-pdp').then(() => this.syncInfo());

      // Reflect whatever is already selected on load.
      this.syncSelectionFromDom();
      this.syncInfo();
    }

    disconnectedCallback() {
      if (this.onScroll) {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }
      if (this.mo) this.mo.disconnect();
      if (this._onBuyState) document.removeEventListener('so:buy-state', this._onBuyState);
    }

    setVisible(v) {
      this.classList.toggle('is-visible', v);
      if (this.card) this.card.tabIndex = v ? 0 : -1; // off-screen bar stays out of the tab order
      this.btn.tabIndex = v ? 0 : -1;
      document.body.classList.toggle('so-sticky-atc-open', v);
      if (v) document.body.style.setProperty('--so-sticky-atc-h', this.offsetHeight + 'px');
    }

    isUnselected() {
      const id = this.mainForm.querySelector('input[name="id"]');
      return !id || !id.value;
    }

    // True while product-form.js is mid add-to-cart (adds .is-loading / aria-disabled to the main button).
    isBusy() {
      return this.mainBtn.getAttribute('aria-disabled') === 'true' || this.mainBtn.classList.contains('is-loading');
    }

    state() {
      // Loading is transient, not terminal: keep the resolved 'add'/'select' semantics.
      if (this.isBusy()) return this.isUnselected() ? 'select' : 'add';
      if (!this.mainBtn.disabled) return 'add';
      return this.isUnselected() ? 'select' : 'soldout';
    }

    setLabel(text) {
      if (this.btn.textContent !== text) this.btn.textContent = text;
    }

    syncButton() {
      const s = this.state();
      this.btn.dataset.state = s;
      if (s === 'add') {
        // Mirror the MAIN button's real label so pre-order shows "Pre-order", not "Add to cart".
        var mainLabel = (this.mainBtn.textContent || '').trim(); // P0-2B: same label, total included
        this.setLabel(mainLabel || this.dataset.addText);
        this.btn.disabled = this.isBusy(); // disable while the add is in flight
      } else if (s === 'select') {
        this.setLabel(this.optionCount > 1 ? this.dataset.selectMultiText : this.dataset.selectSingleText);
        this.btn.disabled = false;
      } else {
        // F2 Q1: a button WE disabled (data-so-disabled: "In your cart", a bundle's "Sold out") keeps its own
        // words in the bar; only the theme's plain sold-out state uses the bar's sold-out text.
        const own = this.mainBtn.dataset.soDisabled ? (this.mainBtn.textContent || '').trim() : '';
        this.setLabel(own || this.dataset.soldoutText);
        this.btn.disabled = true;
      }
    }

    syncPrice() {
      if (this.mainPrice && this.priceEl) {
        // Clone the theme's own (first-party) price node rather than assigning innerHTML.
        const clone = this.mainPrice.cloneNode(true);
        this.priceEl.replaceChildren(...clone.childNodes);
      }
    }

    setVariantName(variant) {
      const name = (variant && variant.title && !variant.title.startsWith('Default')) ? variant.title : '';
      if (this.variantEl) { this.variantEl.textContent = name; this.variantEl.hidden = !name; }
      if (this.thumbEl) {
        const img = variant && (variant.featured_image || variant.image);
        const src = img ? (img.src || img) : '';
        if (src) { this.thumbEl.style.backgroundImage = 'url("' + src + '")'; this.thumbEl.hidden = false; }
        else { this.thumbEl.hidden = true; }
      }
    }

    // Reflect the current selection on load (event only fires on change): prefer the
    // drawer trigger's thumb+name, else the checked native option value.
    syncSelectionFromDom() {
      const trigger = this.product.querySelector('.so-vd__trigger.is-selected');
      if (trigger) {
        const nm = trigger.querySelector('.so-vd__trigger-name');
        const th = trigger.querySelector('.so-vd__trigger-thumb');
        if (nm && this.variantEl) { this.variantEl.textContent = nm.textContent.trim(); this.variantEl.hidden = false; }
        if (th && this.thumbEl && th.style.backgroundImage) { this.thumbEl.style.backgroundImage = th.style.backgroundImage; this.thumbEl.hidden = false; }
        return;
      }
      const checked = this.variantPicker && this.variantPicker.querySelector('.js-option:checked');
      if (checked && this.variantEl) {
        const v = checked.value;
        if (v && v.indexOf('Default') !== 0) { this.variantEl.textContent = v; this.variantEl.hidden = false; }
      }
    }

    // ---- P0-2B: buy-state summary card + edit in the buy box ------------------------------------
    buyState() {
      if (!this.pdp || typeof this.pdp.buyState !== 'function') return null;
      try { return this.pdp.buyState(); } catch (e) { return null; }
    }
    // Card: thumb, "#4 Freezy Grape" (single-axis) or the product's short name (single-variant and
    // multi-axis bundles, whose combined variant titles are too long), "Qty 5" and Edit.
    // Until the shared buy state exists, the legacy name/price show instead.
    syncInfo() {
      const st = this.buyState();
      const live = !!(st && this.card && this.cardName);
      if (this.infoEl) this.infoEl.classList.toggle('is-live', live);
      if (this.card) this.card.hidden = !live;
      if (!live) return;
      const single = this.variantCount > 1 && this.optionCount === 1;
      let name = single && st.title && st.title.indexOf('Default') !== 0 ? st.title : '';
      if (!name && single && this.variantEl) name = this.variantEl.textContent.trim();
      if (!name && single && st.id == null) name = this.dataset.selectSingleText || 'Choose';
      if (!name) name = this.dataset.productName || '';
      this.cardName.textContent = name;
      // Only a real stepper on a buyable variant means the quantity is the shopper's to change
      // (sold out: the card just names the flavour and Edit leads to the picker).
      const hasQty = !!this.mainForm.querySelector('quantity-input [name="quantity"]') && st.addable;
      if (this.qtyBadge) {
        this.qtyBadge.hidden = !hasQty;
        this.qtyBadge.textContent = 'Qty ' + st.qty;
      }
      const bg = this.thumbEl && !this.thumbEl.hidden && this.thumbEl.style.backgroundImage;
      if (this.cardThumb) {
        const src = bg || (this.dataset.productImage ? 'url("' + this.dataset.productImage + '")' : '');
        this.cardThumb.style.backgroundImage = src;
        this.cardThumb.hidden = !src;
      }
      const what = this.variantCount > 1 ? (hasQty ? 'flavour or quantity' : 'options') : 'quantity';
      this.card.setAttribute('aria-label', name + (hasQty ? ', quantity ' + st.qty : '') + '. Edit ' + what + ' in the buy box');
    }
    // Scroll the buy box's picker to just under the header (quantity + main button follow below it)
    // and pulse the picker and quantity. The bar hides itself once the main button is back on screen.
    editInBuyBox() {
      // What the shopper came to change: the drawer trigger (single-axis flavour products) or the
      // option fieldsets (bundles etc.), then the quantity stepper. variant-picker itself is inline.
      let pick = [];
      if (this.variantCount > 1) {
        const trig = Array.prototype.filter.call(this.product.querySelectorAll('.so-vd__trigger'), (t) => !t.closest('quick-add-drawer'))[0];
        pick = trig ? [trig] : (this.variantPicker ? Array.prototype.slice.call(this.variantPicker.querySelectorAll('.option-selector')) : []);
      }
      const qty = this.mainForm.querySelector('quantity-input');
      const top = pick[0] || qty || this.mainForm;
      // Land it just under the sticky header (whatever is pinned at the top right now).
      let offset = 0;
      document.querySelectorAll('.shopify-section-group-header-group').forEach((h) => {
        const pos = getComputedStyle(h).position;
        const r = h.getBoundingClientRect();
        if ((pos === 'sticky' || pos === 'fixed') && r.bottom > offset && r.bottom < window.innerHeight / 3) offset = r.bottom;
      });
      const y = top.getBoundingClientRect().top + window.scrollY - offset - 24;
      window.scrollTo({ top: Math.max(0, y), behavior: this.reduceMotion ? 'auto' : 'smooth' });
      pick.concat(qty ? [qty] : []).forEach((el) => {
        el.classList.add('so-sticky-atc-highlight');
        setTimeout(() => el.classList.remove('so-sticky-atc-highlight'), 2000);
      });
      // Keyboard / screen-reader users land on the first control they came to change.
      const focusEl = pick[0] ? (pick[0].matches('button') ? pick[0] : pick[0].querySelector('input:checked, input, select, button')) : (qty && qty.querySelector('input'));
      if (focusEl) setTimeout(() => focusEl.focus({ preventScroll: true }), this.reduceMotion ? 0 : 450);
    }

    openPicker() {
      const trigger = this.product.querySelector('.so-vd__trigger');
      if (trigger) trigger.click(); else this.scrollToPicker();
    }

    onClick() {
      const s = this.btn.dataset.state;
      if (s === 'add') {
        if (this.mainForm.requestSubmit) this.mainForm.requestSubmit(this.mainBtn);
        else this.mainBtn.click();
      } else if (s === 'select') {
        this.scrollToPicker();
      } else {
        this.scrollTo(this.mainBtn, false);
      }
    }

    scrollToPicker() {
      let target = null;
      if (this.variantPicker) {
        // Works for both selector styles: button/"listed" pickers use <fieldset class="option-selector">
        // with radio inputs (first without a checked input = first unselected); dropdown pickers use
        // <div class="option-selector"> with no radios, so this resolves to the first option group.
        try {
          target = this.variantPicker.querySelector('.option-selector:not(:has(input:checked))');
        } catch (e) {
          target = null;
        }
        target = target || this.variantPicker.querySelector('.option-selector') || this.variantPicker;
      }
      this.scrollTo(target || this.mainForm, true);
    }

    scrollTo(el, highlight) {
      if (!el) return;
      el.scrollIntoView({ behavior: this.reduceMotion ? 'auto' : 'smooth', block: 'center' });
      if (highlight) {
        el.classList.add('so-sticky-atc-highlight');
        setTimeout(() => el.classList.remove('so-sticky-atc-highlight'), 2000);
      }
    }
  }
  customElements.define('so-sticky-atc', SoStickyAtc);
}
